import { setSelectedOpportunityId } from './../actions/set-selected-opportunity-id.actions';
import { setPreferredPeriod } from './../actions/set-preferred-period.actions';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { createCart } from '../actions/create-cart.actions';
import { loadCart } from '../actions/load-cart.actions';
import { createOrder } from './../actions/create-order.actions';
import { createQuotes } from './../actions/create-quotes.actions';
import { loadAutoApplyOfferingDiscounts } from './../actions/load-auto-apply-offering-discounts.actions';
import { Observable } from 'rxjs';
import {
  selectAllCartItemsWithDiscountsForPeriod,
  selectCart,
  selectCartLoadingState,
  selectCartLoadingText,
  selectCartTotal,
  selectCartTotalCartItemOfferingsWithPricing,
  selectApprovalNeededForPeriods,
  selectSelectedCartItem,
  selectSkipExternalCreationAvailableForPeriods,
  selectFirstCartItemOffering,
  selectCustomExpirationDate,
  selectSelectedPeriods,
  selectAllCartItemsOfferingCodes,
  selectHasEsetServicesInCart,
  selectHasServicesValidationErrors,
  selectCartLargeQuantityValidations,
  selectValidationForCart,
} from '../reducers/cart.reducer';
import { selectOfferingDiscountsByOfferingCode } from '../reducers/offering-discounts.reducer';
import { Cart } from '@shared/models/cart';
import { LoadingState } from '@shared/types/loading-state';
import {
  selectAllOfferingDiscounts,
  selectOfferingDiscountsLoadingState,
} from '../reducers/offering-discounts.reducer';
import { OfferingDiscount } from '@shared/models/offering-discount';
import { CartConfig } from '@shared/models/cart-config';
import { setCartConfig } from '../actions/set-cart-config.actions';
import {
  selectCartConfig,
  selectCartConfigLoadingState,
  selectCartId,
  selectCartItemId,
  selectCartCustomer,
  selectCartCustomerOrders,
  selectCartCustomerOrdersLoadingState,
  selectCartCustomerOrdersExpanded,
  selectCartCustomerValidations,
  selectCartCustomerContactByRole,
  selectSelectedOpportunityId,
  selectPreferredPeriod,
} from '../reducers/cart-config.reducer';
import { Group } from '@shared/models/group';
import { selectCartItem } from '../actions/select-cart-item.actions';
import { CartItem } from '@shared/models/cart-item';
import { Period, PricingPeriod } from '@shared/types/period';
import { CartTotalCartItemOfferingWithPricing } from '@shared/models/cart-total-cart-item-offering-with-pricing';
import { CartTotal } from '@shared/models/cart-total';
import { QuoteType } from '@shared/types/quote-type';
import { Offering } from '@shared/models/offering';
import { setCustomExpirationDate } from '../actions/set-custom-expiration-date.actions';
import { updateSelectedPeriods } from '../actions/update-selected-periods.actions';
import { loadCartCustomerOrders } from '../actions/load-cart-customer-orders.actions';
import { setCartCustomerOrdersExpanded } from '../actions/set-cart-customer-orders-expanded.actions';
import { CartCustomerValidations } from '@shared/models/cart-customer-validations';
import { ContactRole } from '@shared/types/contact-role';
import { Contact } from '@shared/models/contact';
import { Order } from '@shared/models/order';

@Injectable({
  providedIn: 'root',
})
export class CartsService {
  constructor(private store: Store) {}

  setCartConfig(cartConfig: CartConfig) {
    this.store.dispatch(setCartConfig({ cartConfig, clearCart: true }));
  }

  selectCartItem(cartItemId: string | undefined) {
    this.store.dispatch(selectCartItem({ cartItemId }));
  }

  setCustomExpirationDate(customExpirationDate: string) {
    this.store.dispatch(setCustomExpirationDate({ customExpirationDate }));
  }

  updateSelectedPeriods(period: PricingPeriod) {
    this.store.dispatch(updateSelectedPeriods({ period }));
  }

  loadCart(id: string) {
    this.store.dispatch(loadCart({ id }));
  }

  createCart(cartConfig: CartConfig) {
    this.store.dispatch(createCart({ cartConfig }));
  }

  loadAutoApplyOfferingDiscounts() {
    this.store.dispatch(loadAutoApplyOfferingDiscounts());
  }

  loadCartCustomerOrders(groupId: string) {
    this.store.dispatch(loadCartCustomerOrders({ groupId }));
  }

  createQuotes(
    quote_type: QuoteType,
    periods: Period[],
    send_email: boolean,
    reference?: string,
    skip_external_creation?: boolean,
    custom_expiration_date?: string,
    end_user_quote_email_address?: string[],
    reseller_quote_email_address?: string[],
    preferred_period?: Period,
    opportunity_id?: number,
  ) {
    this.store.dispatch(
      createQuotes({
        quote_type,
        periods,
        send_email,
        reference,
        skip_external_creation,
        custom_expiration_date,
        end_user_quote_email_address,
        reseller_quote_email_address,
        preferred_period,
        opportunity_id,
      }),
    );
  }

  createOrder(
    quote_type: QuoteType,
    period: Period,
    reference?: string,
    custom_expiration_date?: string,
    opportunity_id?: number,
  ) {
    this.store.dispatch(createOrder({ quote_type, period, reference, custom_expiration_date, opportunity_id }));
  }

  setSelectedOpportunityId(opportunityId: number | undefined) {
    this.store.dispatch(setSelectedOpportunityId({ opportunityId }));
  }

  setPreferredPeriod(period: Period | undefined) {
    this.store.dispatch(setPreferredPeriod({ period }));
  }

  getCart(): Observable<Cart | undefined> {
    return this.store.select(selectCart);
  }

  getCartId(): Observable<string | undefined> {
    return this.store.select(selectCartId);
  }

  getCartItemId(): Observable<string | undefined> {
    return this.store.select(selectCartItemId);
  }

  getSelectedCartItem(): Observable<CartItem | undefined> {
    return this.store.select(selectSelectedCartItem);
  }

  getCartConfig(): Observable<CartConfig | undefined> {
    return this.store.select(selectCartConfig);
  }

  getCartCustomer(): Observable<Group | undefined> {
    return this.store.select(selectCartCustomer);
  }

  getCartCustomerOrders(): Observable<Order[] | undefined> {
    return this.store.select(selectCartCustomerOrders);
  }

  getCartCustomerOrdersLoadingState(): Observable<LoadingState> {
    return this.store.select(selectCartCustomerOrdersLoadingState);
  }

  getCartCustomerOrdersExpanded(): Observable<boolean> {
    return this.store.select(selectCartCustomerOrdersExpanded);
  }

  setCartCustomerOrdersExpanded(expanded: boolean): void {
    this.store.dispatch(setCartCustomerOrdersExpanded({ expanded }));
  }

  getCartCustomerContactByRole(contactRole: ContactRole): Observable<Contact | undefined> {
    return this.store.select(selectCartCustomerContactByRole(contactRole));
  }

  getCartCustomerValidations(): Observable<CartCustomerValidations | undefined> {
    return this.store.select(selectCartCustomerValidations);
  }

  getCartValidations(): Observable<CartCustomerValidations | undefined> {
    return this.store.select(selectValidationForCart);
  }

  getCartLoadingState(): Observable<LoadingState> {
    return this.store.select(selectCartLoadingState);
  }

  getCartLoadingText(): Observable<string | undefined> {
    return this.store.select(selectCartLoadingText);
  }

  getCartConfigLoadingState(): Observable<LoadingState> {
    return this.store.select(selectCartConfigLoadingState);
  }

  getAllCartItemsOfferingCodes(): Observable<string[] | undefined> {
    return this.store.select(selectAllCartItemsOfferingCodes);
  }

  getAutoApplyOfferingDiscounts(): Observable<OfferingDiscount[]> {
    return this.store.select(selectAllOfferingDiscounts);
  }

  getAutoApplyOfferingDiscountsByOfferingCode(offeringCode: string): Observable<OfferingDiscount | undefined> {
    return this.store.select(selectOfferingDiscountsByOfferingCode(offeringCode));
  }

  getAutoApplyOfferingDiscountsLoadingState(): Observable<LoadingState> {
    return this.store.select(selectOfferingDiscountsLoadingState);
  }

  getAllCartItemsWithDiscountsForPeriod(pricingPeriod: PricingPeriod): Observable<CartItem[] | undefined> {
    return this.store.select(selectAllCartItemsWithDiscountsForPeriod(pricingPeriod));
  }

  getCartTotal(pricingPeriod: PricingPeriod): Observable<CartTotal | undefined> {
    return this.store.select(selectCartTotal(pricingPeriod));
  }

  getCartTotalCartItemOfferingsWithPricing(
    pricingPeriod: PricingPeriod,
  ): Observable<CartTotalCartItemOfferingWithPricing[]> {
    return this.store.select(selectCartTotalCartItemOfferingsWithPricing(pricingPeriod));
  }

  getApprovalNeededForPeriods(): Observable<PricingPeriod[]> {
    return this.store.select(selectApprovalNeededForPeriods);
  }

  getSkipExternalCreationAvailableForPeriods(): Observable<boolean> {
    return this.store.select(selectSkipExternalCreationAvailableForPeriods);
  }

  getHasEsetServicesInCart(): Observable<boolean> {
    return this.store.select(selectHasEsetServicesInCart);
  }

  getHasServicesValidationErrors(): Observable<boolean> {
    return this.store.select(selectHasServicesValidationErrors);
  }

  getFirstCartItemOffering(): Observable<Offering | undefined> {
    return this.store.select(selectFirstCartItemOffering);
  }

  getCartLargeQuantityValidations(): Observable<boolean> {
    return this.store.select(selectCartLargeQuantityValidations);
  }

  getCustomExpirationDate(): Observable<string | undefined> {
    return this.store.select(selectCustomExpirationDate);
  }

  getSelectedPeriods(): Observable<PricingPeriod[]> {
    return this.store.select(selectSelectedPeriods);
  }

  getSelectedOpportunityId(): Observable<number | undefined> {
    return this.store.select(selectSelectedOpportunityId);
  }

  getPreferredPeriod(): Observable<Period | undefined> {
    return this.store.select(selectPreferredPeriod);
  }
}
